/**
 * Compare two dot-separated version strings.
 *
 * Replaces the original `eval('0' + comparator + cmp)` implementation with
 * an explicit comparator switch to avoid dynamic code evaluation.
 *
 * @param {string} v1
 * @param {'=='|'==='|'='|'<'|'<='|'>'|'>='|'!='|'!=='} comparator
 * @param {string} v2
 * @returns {boolean}
 */
export function compareVersions(v1, comparator, v2) {
    const op = comparator === '=' ? '==' : comparator;
    const allowed = ['==', '===', '<', '<=', '>', '>=', '!=', '!=='];
    if (allowed.indexOf(op) === -1) {
        throw new Error('Invalid comparator. ' + comparator);
    }

    const v1parts = String(v1).split('.');
    const v2parts = String(v2).split('.');
    const maxLen = Math.max(v1parts.length, v2parts.length);

    // Original convention: cmp = 1 if v1 < v2, -1 if v1 > v2, 0 if equal.
    let cmp = 0;
    for (let i = 0; i < maxLen && !cmp; i++) {
        const part1 = parseInt(v1parts[i], 10) || 0;
        const part2 = parseInt(v2parts[i], 10) || 0;
        if (part1 < part2) cmp = 1;
        if (part1 > part2) cmp = -1;
    }

    switch (op) {
        case '==':
        case '===':
            return 0 === cmp;
        case '!=':
        case '!==':
            return 0 !== cmp;
        case '<':
            return 0 < cmp;
        case '<=':
            return 0 <= cmp;
        case '>':
            return 0 > cmp;
        case '>=':
            return 0 >= cmp;
        default:
            return false;
    }
}

/**
 * Evaluate whether the native app reports a version at or above `minVersion`.
 *
 * @param {{ version?: string }} params - Parameters as returned by the native `getAppParams` action.
 * @param {string} [minVersion='3.0.0']
 * @returns {{ appVersion3: boolean }}
 */
export function checkFeatureAvailability(params, minVersion = '3.0.0') {
    const version = params && params.version ? params.version : '0.0.0';
    const appVersion3 = !compareVersions(version, '<', minVersion);
    return { appVersion3 };
}
